import { Component, AfterViewInit, ViewChild, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';

// Importaciones de servicios.
import { HostService } from '../../../services/host.service';

// Importaciones de componentes.
import { HostCreateDialogComponent } from '../../../components/host-create-dialog/host-create-dialog.component';
import { HostUpdateDialogComponent } from '../../../components/host-update-dialog/host-update-dialog.component';
import { HostDeleteDialogComponent } from '../../../components/host-delete-dialog/host-delete-dialog.component';

// Importaciones de interfaces.
import { IHost } from '../../../interfaces/host.interface';
import { IHostResponse } from '../../../interfaces/host-response.interface';

// Importaciones de modelos.
import { Host } from '../../../models/host.model';

/**
 * Componente de configuración de los hosts.
 *
 * Este componente proporciona una interfaz para gestionar la configuración de los hosts dentro de la aplicación.
 * Permite realizar ajustes relacionados con los hosts, como actualizar información, eliminar o crear.
 */
@Component({
  selector: 'app-host-settings',
  templateUrl: './host-settings.component.html',
  standalone: false,
})
export class HostSettingsComponent implements OnInit, AfterViewInit {
  /**
   * Indica si los hosts están siendo cargados.
   *
   * Esta propiedad se utiliza para mostrar un indicador de carga mientras
   * se recuperan los hosts de la fuente de datos.
   */
  loading: boolean = false;

  /**
   * Columnas que se mostrarán en la tabla de los hosts.
   *
   * Esta propiedad define las columnas que se mostrarán en la tabla de los hosts, incluyendo los campos
   * relacionados con la información básica del host.
   */
  tableColumns: string[] = [
    'name',
    'type',
    'disabled',
    'updatedAt',
    'createdAt',
    'actions',
  ];

  /**
   * Datos de la tabla de los hosts.
   *
   * Esta propiedad contiene los datos de los hosts que se mostrarán en la tabla, utilizando `MatTableDataSource`
   * para manejar y mostrar los datos de manera eficiente, incluyendo funcionalidades como paginación y filtrado.
   */
  tableData: MatTableDataSource<IHost> = new MatTableDataSource<IHost>();

  /**
   * Paginador de la tabla de los hosts.
   *
   * Esta propiedad se utiliza para la paginación de los datos en la tabla de los hosts. Se hace referencia a un
   * componente `MatPaginator` que permite navegar entre las diferentes páginas de la tabla.
   */
  @ViewChild(MatPaginator) tablePaginator: MatPaginator | null = null;

  /**
   * Referencia al componente MatSort que se utiliza para habilitar la funcionalidad de ordenación en la tabla.
   *
   * Se utiliza el decorador `@ViewChild` para acceder a la instancia del componente MatSort asociado a la tabla de los hosts.
   * Esto permite aplicar la funcionalidad de ordenación en las columnas de la tabla, permitiendo a los usuarios ordenar los
   * datos de forma ascendente o descendente según la columna seleccionada.
   *
   * @see MatSort - Componente de Angular Material que proporciona capacidades de ordenación para las tablas.
   */
  @ViewChild(MatSort) tableSort: MatSort | null = null;

  /**
   * Constructor del componente.
   *
   * Este constructor inyecta las dependencias necesarias para el funcionamiento del componente.
   *
   * @param hostService - Servicio para gestionar la información de los hosts.
   * @param matDialog - Servicio para abrir cuadros de diálogo.
   * @param snackBar - Servicio para mostrar notificaciones de tipo snack bar.
   */
  constructor(
    private hostService: HostService,
    private matDialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Método del ciclo de vida de Angular que se ejecuta una vez que el componente
   * ha sido inicializado, pero antes de que se renderice la vista en el DOM.
   *
   * Este método se utiliza para inicializar datos o realizar cualquier operación
   * que deba llevarse a cabo antes de que el componente sea visible para el usuario.
   * En este caso, se llama a la función `initTable()` para inicializar y cargar
   * los datos necesarios para la tabla.
   */
  ngOnInit(): void {
    this.initTable();
  }

  /**
   * Método del ciclo de vida de Angular que se ejecuta después de que
   * la vista del componente y sus vistas hijas hayan sido completamente inicializadas.
   *
   * Este método se utiliza para realizar inicializaciones adicionales que
   * requieren acceso a las vistas del componente (como elementos DOM,
   * directivas, o componentes hijos).
   */
  ngAfterViewInit(): void {
    this.configureTable();
  }

  /**
   * Inicializa la tabla de los hosts.
   *
   * Este método se encarga de configurar y cargar los datos necesarios para la tabla de los hosts.
   * Puede incluir la obtención de datos desde un servicio, la configuración de la paginación
   * y otras configuraciones relacionadas con la visualización de la tabla.
   */
  private async initTable(): Promise<void> {
    this.loading = true;
    this.tableData = new MatTableDataSource<IHost>();

    try {
      // Obtener los hosts y añadir-los a la tabla.
      const hostResponse: IHostResponse = await this.hostService.getAll();

      this.tableData = new MatTableDataSource<IHost>(
        hostResponse.hosts.map((host: IHost) => {
          return new Host(host);
        })
      );
    } finally {
      this.configureTable();
      this.loading = false;
    }
  }

  /**
   * Configura la tabla de datos con las opciones necesarias, como la ordenación y la paginación.
   *
   * Se asegura de que la tabla esté configurada correctamente para su visualización y que las
   * funcionalidades de paginación y ordenación estén habilitadas, si es necesario.
   */
  private configureTable(): void {
    this.tableData.data?.reverse();

    // Asignar el `paginator` y el `sort` si existen.
    this.tablePaginator && (this.tableData.paginator = this.tablePaginator);
    this.tableSort && (this.tableData.sort = this.tableSort);
  }

  /**
   * Recarga la tabla de los hosts.
   *
   * Esta función se encarga de actualizar la vista de la tabla de los hosts
   * para reflejar los cambios recientes en los datos. Puede ser útil después
   * de realizar acciones como agregar, editar o eliminar hosts, asegurando
   * que la interfaz siempre muestre la información más actualizada.
   */
  reload(): void {
    this.initTable();
  }

  /**
   * Crea un nuevo host.
   *
   * Esta función abre un diálogo en la interfaz de usuario para permitir al
   * usuario ingresar los datos del nuevo host. Una vez que el usuario
   * ha completado el formulario y confirmado la acción, la función recoge
   * la información ingresada y crea un nuevo registro de host. Si la
   * creación es exitosa, se actualiza la lista de hosts.
   */
  create(): void {
    this.matDialog
      .open(HostCreateDialogComponent, {
        width: '500px',
        maxWidth: '100%',
      })
      .afterClosed()
      .subscribe((isCreated: boolean) => {
        if (isCreated) {
          this.initTable();
        }
      });
  }

  /**
   * Actualiza la información de un host.
   *
   * Este método se encarga de actualizar los datos de un host específica, generalmente a través
   * de una llamada a un servicio para realizar la actualización en la base de datos o en el servidor.
   * El parámetro `host` contiene los nuevos valores que se deben guardar.
   *
   * @param host - El objeto que contiene la información del host.
   */
  update(host: IHost) {
    this.matDialog
      .open(HostUpdateDialogComponent, {
        width: '500px',
        maxWidth: '100%',
        data: host,
      })
      .afterClosed()
      .subscribe((isUpdated: boolean) => {
        if (isUpdated) {
          this.initTable();
        }
      });
  }

  /**
   * Elimina un host de la lista.
   *
   * Abre un diálogo de confirmación para eliminar un host de la lista.
   * Si el usuario confirma la acción, el host es eliminado.
   *
   * @param host - El objeto host que se va a eliminar.
   */
  delete(host: IHost): void {
    this.matDialog
      .open(HostDeleteDialogComponent, {
        width: '500px',
        maxWidth: '100%',
        data: host,
      })
      .afterClosed()
      .subscribe((isDeleted: boolean) => {
        if (isDeleted) {
          this.initTable();
        }
      });
  }

  /**
   * Actualiza el estado de `host.disabled` y lo guarda en la base de datos.
   * Revierte el cambio si ocurre un error.
   *
   * @param host - Objeto a actualizar.
   * @param isChecked - Nuevo estado del toggle.
   */
  async toggleStatus(host: IHost, isChecked: boolean): Promise<void> {
    const previousValue = host.disabled;
    host.disabled = Number(!isChecked);

    try {
      const hostResponse: string = await this.hostService.update(host);
      this.snackBar.open(hostResponse, '', {
        duration: 5000,
      });
    } catch (errors: any) {
      host.disabled = previousValue;
    }
  }
}
